/**
 * PWA Update Prompt Component
 * Shows update notification when new service worker version is available
 * Allows users to update to the latest version
 */

import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { skipWaiting, isUpdateAvailable, onUpdateAvailable, offUpdateAvailable, getRegistration } from '../utils/serviceWorkerRegistration';

const UPDATE_STICKY_KEY = 'pwa-update-available';

// Check if we're on a public page (where auto-updates should be disabled)
const isPublicPage = () => {
  const publicPaths = ['/', '/landing', '/login', '/signup', '/about', '/contact-form', '/terms-of-service', '/privacy-policy', '/unsubscribe'];
  const currentPath = window.location.pathname;
  // Public trainer profile pages (not authenticated routes)
  const isPublicTrainerProfile = currentPath.startsWith('/trainer/') && 
                                 !currentPath.includes('/trainer/home') && 
                                 !currentPath.includes('/trainer/classes') && 
                                 !currentPath.includes('/trainer/programs') &&
                                 !currentPath.includes('/trainer/fees') &&
                                 !currentPath.includes('/trainer/students') &&
                                 !currentPath.includes('/trainer/settings') &&
                                 !currentPath.includes('/trainer/notifications');
  return publicPaths.includes(currentPath) || isPublicTrainerProfile;
};

const PWAUpdatePrompt = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const autoUpdateAttemptedRef = useRef(false); // Prevent multiple auto-update attempts
  const updateShownRef = useRef(false); // Track if we've shown the notification for current update
  const waitingWorkerRef = useRef(null); // Track the waiting service worker ID
  const userDismissedRef = useRef(false); // Track if user explicitly dismissed the notification

  useEffect(() => {
    // Check if app is running in standalone mode (PWA installed)
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
      setIsStandalone(standalone);
      return standalone;
    };

    // Auto-update function for browser mode
    const handleAutoUpdate = async () => {
      // Prevent multiple auto-update attempts
      if (autoUpdateAttemptedRef.current) {
        console.log('Auto-update already attempted, skipping...');
        return;
      }
      
      // Don't auto-update on public pages to avoid disrupting user experience
      if (isPublicPage()) {
        console.log('Skipping auto-update on public page to prevent reload loops');
        return;
      }
      
      try {
        autoUpdateAttemptedRef.current = true;
        console.log('Auto-updating service worker...');
        await skipWaiting();
        // Page will reload automatically via controllerchange event
      } catch (error) {
        console.error('Error auto-updating service worker:', error);
        autoUpdateAttemptedRef.current = false; // Reset on error
        // Don't reload on error - let user continue using the app
      }
    };

    checkStandalone();

    // If we just reloaded due to SW update, clear sticky flag.
    // (serviceWorkerRegistration sets sessionStorage sw-update-reload before reloading)
    try {
      const justReloaded = sessionStorage.getItem('sw-update-reload');
      if (justReloaded) {
        sessionStorage.removeItem('sw-update-reload');
        localStorage.removeItem(UPDATE_STICKY_KEY);
      }
    } catch {
      // ignore
    }

    // If we previously detected an update, keep showing until user clicks "Update Now".
    try {
      if (localStorage.getItem(UPDATE_STICKY_KEY) === '1' && checkStandalone()) {
        setShowUpdate(true);
        updateShownRef.current = true;
      }
    } catch {
      // ignore
    }

    // Listen for service worker updates
    const handleUpdateAvailable = async () => {
      const isStandaloneMode = checkStandalone();
      
      // Verify there's actually a waiting service worker
      const registration = await getRegistration();
      // IMPORTANT: Do NOT hide the prompt just because "waiting" isn't present momentarily.
      // We'll show (and keep showing) a sticky prompt in standalone mode.
      
      // Track the waiting worker to detect if it changes (if present)
      const waitingWorkerId = registration?.waiting?.scriptURL || null;
      
      // In browser mode (not standalone), auto-update without showing prompt
      // BUT only if we're not on a public page (to prevent reload loops)
      if (!isStandaloneMode) {
        if (!isPublicPage()) {
        console.log('Update available in browser mode - auto-updating...');
        handleAutoUpdate();
        } else {
          console.log('Update available but on public page - skipping auto-update to prevent reload loops');
        }
        return;
      }
      
      // In standalone mode (PWA installed), show update prompt
      // Only show if this is a new update (different worker) or we haven't shown it yet
      if (!updateShownRef.current || (waitingWorkerId && waitingWorkerRef.current !== waitingWorkerId)) {
        console.log('Update available in standalone mode - showing prompt');
        if (waitingWorkerId) {
        waitingWorkerRef.current = waitingWorkerId;
        }
        updateShownRef.current = true;
        userDismissedRef.current = false; // Reset dismissal flag for new update
        setShowUpdate(true);
        try {
          localStorage.setItem(UPDATE_STICKY_KEY, '1');
        } catch {
          // ignore
        }
      } else {
        // Update is still available and notification is already shown - keep it visible
        // Don't hide it - user needs to be able to click the button
        if (!userDismissedRef.current) {
          setShowUpdate(true);
        }
      }
    };

    // Check if service worker is registered and update is available
    const checkForUpdates = async () => {
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            // Check if there's a waiting service worker (actual update available)
            if (registration.waiting) {
              const isStandaloneMode = checkStandalone();
              const waitingWorkerId = registration.waiting.scriptURL;
              
              // In browser mode, auto-update immediately (only once)
              // BUT only if we're not on a public page (to prevent reload loops)
              if (!isStandaloneMode && !autoUpdateAttemptedRef.current) {
                if (!isPublicPage()) {
                console.log('Update available in browser mode - auto-updating...');
                handleAutoUpdate();
                // Don't check for more updates after initiating auto-update
                return;
                } else {
                  console.log('Update available but on public page - skipping auto-update to prevent reload loops');
                }
              } else if (isStandaloneMode) {
                // In standalone mode, show prompt if update is available
                // Only show if this is a new update (different worker) or we haven't shown it yet
                if (isUpdateAvailable() && (!updateShownRef.current || waitingWorkerRef.current !== waitingWorkerId)) {
                  waitingWorkerRef.current = waitingWorkerId;
                  updateShownRef.current = true;
                  userDismissedRef.current = false; // Reset dismissal flag for new update
                  setShowUpdate(true);
                } else if (updateShownRef.current && waitingWorkerRef.current === waitingWorkerId) {
                  // Update is still available and we've already shown it - keep it visible
                  // This ensures the notification doesn't disappear
                  if (!userDismissedRef.current) {
                    setShowUpdate(true);
                  }
                } else if (updateShownRef.current && !userDismissedRef.current) {
                  // Notification was shown before - keep it visible even if check fails temporarily
                  // This prevents the notification from disappearing before user can click
                  setShowUpdate(true);
                }
              }
            } else {
              // No waiting service worker
              // Only reset if this is a different update (worker ID changed) or update was actually applied
              if (waitingWorkerRef.current) {
                // There was a waiting worker before, but now it's gone
                // This could mean the update was applied or the worker was cleared
                // Only hide if we're sure the update is gone (not just temporarily)
                const currentController = navigator.serviceWorker.controller;
                if (currentController && currentController.scriptURL !== waitingWorkerRef.current) {
                  // New controller is active - update was applied
                  console.log('Update was applied - new controller active');
                  updateShownRef.current = false;
                  waitingWorkerRef.current = null;
                  setShowUpdate(false);
                } else {
                  // Worker is gone but no new controller - might be temporary
                  // Keep notification visible if it was already shown (don't hide it prematurely)
                  console.log('Waiting worker gone but no new controller - keeping notification visible if shown');
                  if (updateShownRef.current && !userDismissedRef.current) {
                    setShowUpdate(true); // Keep it visible - don't hide before user can click
                  }
                }
              }
              
              // Reset auto-update flag only if no update is pending
              if (!updateShownRef.current) {
                autoUpdateAttemptedRef.current = false;
              }
            }
            
            // Only check for updates if we haven't attempted auto-update
            // This prevents triggering another update check that could cause a loop
            if (!autoUpdateAttemptedRef.current) {
              // Check for updates (this will trigger updatefound event if update exists)
              await registration.update();
            }
          }
        } catch (error) {
          console.error('Error checking for updates:', error);
        }
      }
    };

    // Check if we just reloaded due to service worker update
    // If so, skip the initial update check to prevent loops
    const justReloaded = (() => {
      try {
        return sessionStorage.getItem('sw-update-reload');
      } catch {
        return null;
      }
    })();
    let initialCheckTimeout = null;
    
    if (justReloaded) {
      console.log('Just reloaded after SW update, skipping initial update check');
    } else {
      // Wait a bit before checking for updates on mount to prevent immediate re-check after reload
      // This prevents refresh loops
      initialCheckTimeout = setTimeout(() => {
    checkForUpdates();
      }, 2000); // Wait 2 seconds after mount
    }

    // Listen for update availability
    onUpdateAvailable(handleUpdateAvailable);

    // Check for updates periodically (but not too frequently to avoid loops)
    const updateInterval = setInterval(() => {
      // Only check if we haven't attempted auto-update
      if (!autoUpdateAttemptedRef.current) {
        checkForUpdates();
      }
    }, 60 * 60 * 1000); // Check every hour

    // Cleanup
    return () => {
      if (initialCheckTimeout) {
        clearTimeout(initialCheckTimeout);
      }
      offUpdateAvailable(handleUpdateAvailable);
      clearInterval(updateInterval);
    };
  }, []);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      // Skip waiting and activate new service worker
      await skipWaiting();
      // Reset flags since update is being applied
      updateShownRef.current = false;
      waitingWorkerRef.current = null;
      userDismissedRef.current = false;
      try {
        localStorage.removeItem(UPDATE_STICKY_KEY);
      } catch {
        // ignore
      }
      // Page will reload automatically
    } catch (error) {
      console.error('Error updating service worker:', error);
      toast.error('Failed to update. Please refresh the page.', {
        position: 'top-center',
        autoClose: 3000,
      });
      setIsUpdating(false);
    }
  };

  // Don't show update prompt if:
  // 1. showUpdate is false
  // 2. We're in browser mode (should auto-update instead)
  // Always hide in browser mode - updates should be automatic
  if (!showUpdate || !isStandalone) {
    return null;
  }

  return (
    <div className="fixed top-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-[10001] animate-slide-down">
      <div className="bg-blue-50 border-2 border-blue-300 rounded-lg shadow-xl p-4 flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-blue-900 mb-1">
            Update Required
          </h3>
          <p className="text-sm text-blue-700 mb-3">
            A new version of Yogvital is available. Please update now to continue using the app with the latest features and improvements.
          </p>

          {/* Action Button - Only Update Button, no cancel */}
          <button
            onClick={handleUpdate}
            disabled={isUpdating}
            className="w-full bg-blue-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-blue-700 active:bg-blue-800 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          >
            {isUpdating ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Updating...
              </span>
            ) : (
              'Update Now'
            )}
          </button>
        </div>
      </div>

      {/* Add CSS animation */}
      <style>{`
        @keyframes slide-down {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PWAUpdatePrompt;

